import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';

// ---- W3-X data in/out: imports, integration API keys, webhooks, retention ----

export type ImportKind = 'CUSTOMERS' | 'SUPPLIERS' | 'ITEMS' | 'OPENING_AR' | 'OPENING_AP' | 'TRANSACTIONS';   // F11 (11a): TRANSACTIONS

export interface ImportTemplate {
    kind: ImportKind; label: string; columns: string[]; requiredColumns: string[]; sampleRows: string[][];
    notes: string; needsGoLiveDate: boolean;
}
export interface ImportRow { rowNo: number; status: 'OK' | 'ERROR' | 'SKIPPED'; key: string; messages: string[]; }
export interface ImportJobResult {
    jobId: string; kind: ImportKind; status: 'DRY_RUN' | 'COMMITTED' | 'FAILED'; fileName: string | null;
    totalRows: number; validRows: number; errorRows: number; skippedRows: number; createdCount: number;
    batchErrors: string[]; rows: ImportRow[];
}
export interface ImportJobSummary {
    id: string; kind: ImportKind; fileName: string | null; status: string; totalRows: number; validRows: number;
    errorRows: number; createdCount: number; createdAt: string | null; uploadedBy: string | null;
}

export type IntegrationScope = 'READ_ACCOUNTS' | 'READ_TRIAL_BALANCE' | 'READ_INVOICES' | 'READ_RECEIPTS' | 'READ_BILLS' | 'READ_PAYMENTS';
export const INTEGRATION_SCOPES: IntegrationScope[] = ['READ_ACCOUNTS', 'READ_TRIAL_BALANCE', 'READ_INVOICES', 'READ_RECEIPTS', 'READ_BILLS', 'READ_PAYMENTS'];
export interface ApiKeyView {
    id: string; name: string; keyPrefix: string; scopes: string[]; expiresAt: string | null; expiresOn: string | null; lastUsedAt: string | null;
    revoked: boolean; revokedAt: string | null; createdAt: string | null;
}
export interface CreatedApiKey { key: ApiKeyView; rawKey: string; }

export interface WebhookSettings { events: string[]; configured: boolean; schedulerEnabled: boolean; }
export interface WebhookEndpointView {
    id: string; name: string; url: string; events: string[]; active: boolean; lastSuccessAt: string | null;
    lastFailureAt: string | null; createdAt: string | null; pendingCount: number;
}
export interface WebhookWithSecret { endpoint: WebhookEndpointView; secret: string; }
export interface WebhookDeliveryView {
    id: string; endpointId: string; eventType: string; status: 'PENDING' | 'SENT' | 'FAILED'; attempts: number;
    nextAttemptAt: string | null; lastResponseCode: number | null; lastError: string | null; sentAt: string | null; createdAt: string | null;
}

export interface RetentionPolicyView { id: string; documentClass: string; label: string; years: number; legalHold: boolean; note: string | null; }
export interface RetentionDefaultsResult { created: number; policies: RetentionPolicyView[]; verifyNotice: string; }
export interface RetentionReportRow {
    documentClass: string; label: string; years: number; legalHold: boolean; cutoff: string; pastRetentionCount: number;
    oldest: string | null; held: boolean;
}
export interface RetentionClassInfo { documentClass: string; label: string; suggestedYears: number; }

const institution = () => AuthService.getSelectedInstitution()!;

export const DataImportService = {
    template: async (kind: ImportKind): Promise<ImportTemplate> =>
        (await axios.get(ENDPOINTS.DATA_IMPORT.TEMPLATE(institution(), kind))).data,
    dryRun: async (kind: ImportKind, file: File, goLiveDate?: string): Promise<ImportJobResult> =>
        upload(ENDPOINTS.DATA_IMPORT.DRY_RUN(institution(), kind), file, goLiveDate),
    commit: async (kind: ImportKind, file: File, goLiveDate?: string): Promise<ImportJobResult> =>
        upload(ENDPOINTS.DATA_IMPORT.COMMIT(institution(), kind), file, goLiveDate),
    history: async (kind: ImportKind): Promise<ImportJobSummary[]> =>
        (await axios.get(ENDPOINTS.DATA_IMPORT.HISTORY(institution(), kind))).data,
    // FW-1: CSV of the live rows (customers, suppliers) with the template's columns, status included
    exportCsv: async (kind: ImportKind): Promise<Blob> =>
        (await axios.get(ENDPOINTS.DATA_IMPORT.EXPORT(institution(), kind), { responseType: 'blob' })).data,
    job: async (jobId: string): Promise<ImportJobResult> => (await axios.get(ENDPOINTS.DATA_IMPORT.JOB(jobId))).data,
};

const upload = async (url: string, file: File, goLiveDate?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(url, formData, {
        params: goLiveDate ? { goLiveDate } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const IntegrationKeyService = {
    list: async (): Promise<ApiKeyView[]> => (await axios.get(ENDPOINTS.INTEGRATION_KEY.LIST(institution()))).data,
    create: async (name: string, scopes: string[], expiresOn?: string): Promise<CreatedApiKey> =>
        (await axios.post(ENDPOINTS.INTEGRATION_KEY.CREATE, { institutionId: institution(), name, scopes, expiresOn: expiresOn || null })).data,
    revoke: async (keyId: string): Promise<ApiKeyView> => (await axios.post(ENDPOINTS.INTEGRATION_KEY.REVOKE(keyId))).data,
};

export const WebhookService = {
    settings: async (): Promise<WebhookSettings> => (await axios.get(ENDPOINTS.WEBHOOK.SETTINGS(institution()))).data,
    list: async (): Promise<WebhookEndpointView[]> => (await axios.get(ENDPOINTS.WEBHOOK.LIST(institution()))).data,
    create: async (body: { name: string; url: string; events: string[]; active: boolean }): Promise<WebhookWithSecret> =>
        (await axios.post(ENDPOINTS.WEBHOOK.CREATE, { institutionId: institution(), ...body })).data,
    update: async (id: string, body: { name: string; url: string; events: string[]; active: boolean }): Promise<WebhookEndpointView> =>
        (await axios.put(ENDPOINTS.WEBHOOK.ONE(id), body)).data,
    rotateSecret: async (id: string): Promise<WebhookWithSecret> => (await axios.post(ENDPOINTS.WEBHOOK.ROTATE(id))).data,
    remove: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.WEBHOOK.ONE(id)); },
    test: async (id: string): Promise<WebhookDeliveryView> => (await axios.post(ENDPOINTS.WEBHOOK.TEST(id))).data,
    deliveries: async (endpointId?: string): Promise<WebhookDeliveryView[]> =>
        (await axios.get(ENDPOINTS.WEBHOOK.DELIVERIES(institution()), { params: endpointId ? { endpointId } : undefined })).data,
    retry: async (deliveryId: string): Promise<WebhookDeliveryView> => (await axios.post(ENDPOINTS.WEBHOOK.RETRY(deliveryId))).data,
    sendNow: async (): Promise<{ attempted: number; configured: boolean }> =>
        (await axios.post(ENDPOINTS.WEBHOOK.SEND_NOW(institution()))).data,
};

export const RetentionService = {
    list: async (): Promise<RetentionPolicyView[]> => (await axios.get(ENDPOINTS.RETENTION.LIST(institution()))).data,
    classes: async (): Promise<RetentionClassInfo[]> => (await axios.get(ENDPOINTS.RETENTION.CLASSES(institution()))).data,
    loadDefaults: async (): Promise<RetentionDefaultsResult> => (await axios.post(ENDPOINTS.RETENTION.LOAD_DEFAULTS(institution()))).data,
    save: async (body: { documentClass: string; years: number; legalHold: boolean; note?: string }): Promise<RetentionPolicyView> =>
        (await axios.put(ENDPOINTS.RETENTION.SAVE(institution()), body)).data,
    report: async (): Promise<RetentionReportRow[]> => (await axios.get(ENDPOINTS.RETENTION.REPORT(institution()))).data,
};
